/**
 * waap-aiwaf-general-tab.tsx — AI-WAF General Protection tab (B3 batch).
 *
 * Shows the vendor's managed WAF strategy catalog and the current protection
 * mode level (1-9). Operators can apply a strategy to all websites or change
 * the protection mode — both via ActAsMutationConfirm (reason required).
 *
 * Data sources:
 *   GET /waap/domains/:anchorId/strategies           (catalog)
 *   GET /waap/domains/:anchorId/strategies/mapping   (per-policy applied sites)
 *   GET /waap/domains/:anchorId/mode                 (current level)
 *   PUT /waap/domains/:anchorId/mode                 (set level)
 *   POST /waap/domains/:anchorId/strategies/:type/apply
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, ChevronRight } from 'lucide-react';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { EmptyState } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { ActAsMutationConfirm } from '@/pages/operations/detail/cdn/act-as-mutation-confirm';
import { actAsClient } from '@/api/act-as-client';
import { actAsWAAPKeys } from '@/api/act-as-waap';
import {
  useActAsWAAPStrategies,
  useActAsWAAPStrategyMapping,
  useActAsWAAPMode,
  type WafStrategyRow,
} from '@/api/act-as-waap-account';

/* ── Types ───────────────────────────────────────────────────────────────── */

interface PendingMode {
  kind: 'mode';
  level: number;
}

interface PendingApply {
  kind: 'apply';
  strategy: WafStrategyRow;
}

type PendingMutation = PendingMode | PendingApply;

/* ── Sub-components ──────────────────────────────────────────────────────── */

/** Single strategy card showing name, description, and how many sites applied. */
function StrategyCard({
  strategy,
  appliedCount,
  onApply,
}: {
  strategy: WafStrategyRow;
  appliedCount: number;
  onApply: (s: WafStrategyRow) => void;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ fontSize: 'var(--vn-font-size-sm)', display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <Shield size={14} style={{ color: 'var(--vn-fg-muted)', flexShrink: 0 }} />
          {strategy.name}
        </CardTitle>
        {strategy.description && (
          <CardDescription style={{ fontSize: 'var(--vn-font-size-xs)' }}>
            {strategy.description}
          </CardDescription>
        )}
      </CardHeader>
      <CardBody style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-3)' }}>
        <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
          {t('waap_domain.aiwaf_general_sites_applied', '{{count}} site(s) applied', { count: appliedCount })}
        </span>
        <Button size="sm" variant="secondary" onClick={() => onApply(strategy)}>
          {t('waap_domain.aiwaf_general_apply', 'Apply')}
          <ChevronRight size={12} />
        </Button>
      </CardBody>
    </Card>
  );
}

/* ── Component ───────────────────────────────────────────────────────────── */

interface WaapAiwafGeneralTabProps {
  anchorId: string;
}

/**
 * AI-WAF General Protection tab.
 * Renders managed WAF strategy catalog + current mode picker.
 * Mutations: "Set mode" (PUT /mode) and "Apply strategy" (POST /strategies/:type/apply).
 */
export function WaapAiwafGeneralTab({ anchorId }: WaapAiwafGeneralTabProps) {
  const { t } = useTranslation();

  const strategiesQ = useActAsWAAPStrategies(anchorId);
  const mappingQ    = useActAsWAAPStrategyMapping(anchorId);
  const modeQ       = useActAsWAAPMode(anchorId);

  // Local draft for the mode picker — initialised from server when loaded.
  const [draftLevel, setDraftLevel] = useState<string>('');
  const [pending, setPending]       = useState<PendingMutation | null>(null);

  const isLoading = strategiesQ.isLoading || modeQ.isLoading;
  const isError   = strategiesQ.isError   || modeQ.isError;

  if (isLoading) return <TabLoading rows={4} />;
  if (isError) {
    return (
      <TabError
        title={t('waap_domain.aiwaf_general_load_error', 'Failed to load AI-WAF General')}
        onRetry={() => { strategiesQ.refetch(); modeQ.refetch(); }}
      />
    );
  }

  const strategies = strategiesQ.data ?? [];
  const mapping    = mappingQ.data ?? [];
  const currentLevel = modeQ.data?.level ?? 0;

  // Build applied-site count lookup by strategy_type.
  const appliedCountByType: Record<string, number> = {};
  for (const row of mapping) {
    appliedCountByType[row.strategy_type] = row.website_ids?.length ?? 0;
  }

  // Effective draft level: local draft if set, else server value.
  const effectiveLevel = draftLevel !== '' ? draftLevel : String(currentLevel);

  const LEVELS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  function handleSetMode() {
    const level = parseInt(effectiveLevel, 10);
    if (isNaN(level)) return;
    setPending({ kind: 'mode', level });
  }

  function handleApplyStrategy(strategy: WafStrategyRow) {
    setPending({ kind: 'apply', strategy });
  }

  // Build mutation fn for current pending action.
  function buildMutationFn() {
    if (!pending) return async () => {};
    if (pending.kind === 'mode') {
      return () => actAsClient.put(`/waap/domains/${anchorId}/mode`, { level: pending.level });
    }
    return () =>
      actAsClient.post(`/waap/domains/${anchorId}/strategies/${pending.strategy.strategy_type}/apply`);
  }

  const mutationTitle =
    pending?.kind === 'mode'
      ? t('waap_domain.aiwaf_general_mode_title', 'Set protection mode')
      : t('waap_domain.aiwaf_general_apply_title', 'Apply WAF strategy');

  const mutationSummary =
    pending?.kind === 'mode'
      ? t(
          'waap_domain.aiwaf_general_mode_summary',
          'Set WAF protection level to {{level}} for all websites in this account.',
          { level: pending.level },
        )
      : t(
          'waap_domain.aiwaf_general_apply_summary',
          'Apply "{{name}}" strategy to all websites in this account.',
          { name: pending?.kind === 'apply' ? pending.strategy.name : '' },
        );

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-6)' }}>

      {/* ── Protection Mode ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t('waap_domain.aiwaf_general_mode_heading', 'Protection Mode')}</CardTitle>
          <CardDescription>
            {t(
              'waap_domain.aiwaf_general_mode_desc',
              'Level 1 (off) → Level 9 (most strict). Applies account-wide.',
            )}
          </CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'flex', gap: 'var(--vn-space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field
            label={t('waap_domain.aiwaf_general_level_label', 'Level (1–9)')}
            style={{ minWidth: 140 }}
          >
            {(props) => (
              <Select value={effectiveLevel} onValueChange={setDraftLevel}>
                <SelectTrigger {...props}>
                  <SelectValue placeholder={t('waap_domain.aiwaf_general_level_placeholder', 'Select level')} />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Button
            variant="primary"
            size="sm"
            disabled={effectiveLevel === String(currentLevel)}
            onClick={handleSetMode}
          >
            {t('waap_domain.aiwaf_general_set_mode', 'Set Mode')}
          </Button>
        </CardBody>
      </Card>

      {/* ── Strategy Catalog ─────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        <CardTitle>
          {t('waap_domain.aiwaf_general_catalog_heading', 'Managed Strategies')}
        </CardTitle>

        {strategies.length === 0 ? (
          <EmptyState
            title={t('waap_domain.aiwaf_general_catalog_empty', 'No strategies available')}
            description={t(
              'waap_domain.aiwaf_general_catalog_empty_desc',
              'The vendor has not configured any managed WAF strategies for this account.',
            )}
          />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 'var(--vn-space-3)',
            }}
          >
            {strategies.map((s) => (
              <StrategyCard
                key={s.strategy_type}
                strategy={s}
                appliedCount={appliedCountByType[s.strategy_type] ?? 0}
                onApply={handleApplyStrategy}
              />
            ))}
          </div>
        )}
      </div>

      {strategies.length > 0 && (
        <Alert tone="info" style={{ fontSize: 'var(--vn-font-size-xs)' }}>
          {t(
            'waap_domain.aiwaf_general_custom_hidden',
            'Custom WAF policies (SHOW_CUSTOM_WAF_POLICY = false) are hidden per vendor configuration.',
          )}
        </Alert>
      )}

      {/* ── Mutation confirm dialog ─────────────────────────────────────── */}
      <ActAsMutationConfirm
        open={pending !== null}
        onOpenChange={(open) => { if (!open) setPending(null); }}
        title={mutationTitle}
        summary={mutationSummary}
        queryKeysToInvalidate={[
          actAsWAAPKeys.aspect(anchorId, 'mode'),
          actAsWAAPKeys.aspect(anchorId, 'strategies/mapping'),
        ]}
        mutationFn={buildMutationFn()}
        onSuccess={() => {
          modeQ.refetch();
          mappingQ.refetch();
          setDraftLevel('');
          setPending(null);
        }}
      />
    </div>
  );
}
